import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar,
} from 'recharts';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders(): Record<string, string> {
  const token = JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state?.token ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtUGX(n: number) {
  return 'UGX ' + Math.round(n).toLocaleString('en-UG');
}

function fmtShort(n: number) {
  if (n >= 1_000_000) return `UGX ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `UGX ${(n / 1_000).toFixed(0)}K`;
  return fmtUGX(n);
}

function Skeleton({ h = 28 }: { h?: number }) {
  return (
    <div style={{
      height: h, borderRadius: 10,
      background: 'var(--surface2)', animation: 'pulse 1.5s infinite',
    }} />
  );
}

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) {
  if (!active || !payload?.length || !payload[0]) return null;
  return (
    <div className="card" style={{ padding: '8px 12px', fontSize: 12 }}>
      <p style={{ color: 'var(--text-3)', marginBottom: 2 }}>{label}</p>
      <p style={{ fontWeight: 600, color: 'var(--text)' }}>{fmtShort(payload[0].value)}</p>
    </div>
  );
}

export function DashboardPage() {
  const today = new Date().toISOString().split('T')[0];

  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['daily-summary', today],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/finance/daily-summary?date=${today}`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message ?? 'Error');
      return json.data;
    },
    refetchInterval: 60_000,
  });

  const { data: efrisStatus, isLoading: loadingEfris } = useQuery({
    queryKey: ['efris-status'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/finance/efris/status`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message ?? 'Error');
      return json.data;
    },
    refetchInterval: 120_000,
  });

  const { data: hourlyData, isLoading: loadingHourly } = useQuery({
    queryKey: ['hourly-revenue', today],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/reports/hourly/range?from=${today}&to=${today}`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message ?? 'Error');
      return (json.data.buckets ?? []) as { hour: number; label: string; totalRevenueUgx: string }[];
    },
  });

  const { data: waiterData, isLoading: loadingWaiter } = useQuery({
    queryKey: ['waiter-leaderboard', today],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/reports/waiter-performance?from=${today}&to=${today}`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message ?? 'Error');
      return (json.data.leaderboard ?? []) as { waiterId: string; waiterName: string; totalRevenueUgx: string; orderCount: number }[];
    },
  });

  const { data: lowStock } = useQuery({
    queryKey: ['low-stock'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/inventory/items/low-stock`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message ?? 'Error');
      return (json.data as { name: string; currentStock: string; reorderLevel: string; unit: string }[]).map((i) => ({
        name: i.name, unit: i.unit,
        stockQty: Number(i.currentStock), reorderLevel: Number(i.reorderLevel),
      }));
    },
  });

  const chartData = hourlyData?.map((h) => ({ hour: h.label, revenue: Number(h.totalRevenueUgx) })) ?? [];

  const paymentBreakdown = summary
    ? [
        { method: 'Cash', amount: Number(summary.cashUgx ?? 0) },
        { method: 'MoMo', amount: Number(summary.momoUgx ?? 0) },
        { method: 'Card', amount: Number(summary.cardUgx ?? 0) },
      ].filter((p) => p.amount > 0)
    : [];

  const TICK = { fontSize: 11, fill: 'var(--text-3)' };
  const GRID = { strokeDasharray: '3 3', stroke: 'var(--border)' };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
          <span style={{ color: 'var(--text-3)', fontSize: 12 }}>Live</span>
        </div>
      </div>

      {/* KPI */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        {loadingSummary ? (
          <>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={110} />)}</>
        ) : (
          <>
            {[
              { label: "Today's Revenue", value: fmtShort(Number(summary?.totalRevenueUgx ?? 0)), sub: `${summary?.ordersCount ?? 0} orders`, accent: 'accent' },
              { label: 'VAT Collected', value: fmtShort(Number(summary?.totalVatUgx ?? 0)), sub: '18% standard rate', accent: '' },
              { label: 'Orders Today', value: String(summary?.ordersCount ?? 0), sub: `${summary?.voidedCount ?? 0} voided`, accent: '' },
              {
                label: 'EFRIS Receipts',
                value: loadingEfris ? '…' : String(efrisStatus?.recentSubmitted?.length ?? 0),
                sub: efrisStatus?.unsubmittedCount ? `${efrisStatus.unsubmittedCount} pending` : 'All submitted',
                accent: efrisStatus?.unsubmittedCount ? 'red' : 'green',
              },
            ].map((kpi) => (
              <div key={kpi.label} className={`stat-card${kpi.accent ? ` ${kpi.accent}` : ''}`}>
                <p className="stat-label">{kpi.label}</p>
                <p className="stat-value">{kpi.value}</p>
                {kpi.sub && <p className="stat-sub">{kpi.sub}</p>}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Charts row */}
      <div className="dash-charts-row">
        {/* Hourly revenue */}
        <div className="card">
          <div className="dash-chart-header">
            <div>
              <h2 style={{ fontSize: 15 }}>Hourly Revenue</h2>
              <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 2 }}>Today's sales by hour</p>
            </div>
          </div>
          {loadingHourly ? <Skeleton h={210} /> : chartData.length === 0 ? (
            <div style={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 13 }}>No data yet for today</div>
          ) : (
            <ResponsiveContainer width="100%" height={210}>
              <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="hour" tick={TICK} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => fmtShort(v)} tick={TICK} axisLine={false} tickLine={false} width={72} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="revenue" stroke="var(--accent)" strokeWidth={2} fill="url(#revGrad)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Payment methods */}
        <div className="card">
          <div className="dash-chart-header">
            <div>
              <h2 style={{ fontSize: 15 }}>Payment Methods</h2>
              <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 2 }}>Today's split</p>
            </div>
          </div>
          {loadingSummary ? <Skeleton h={210} /> : paymentBreakdown.length === 0 ? (
            <div style={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 13 }}>No payments yet</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={paymentBreakdown} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="method" tick={TICK} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => fmtShort(v)} tick={TICK} axisLine={false} tickLine={false} width={64} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="amount" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {paymentBreakdown.map((p) => (
                  <div key={p.method} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: 'var(--text-2)' }}>{p.method}</span>
                    <span style={{ fontWeight: 600, color: 'var(--text)' }}>{fmtShort(p.amount)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="dash-bottom-row">
        {/* Waiter leaderboard */}
        <div className="card" style={{ gridColumn: 'span 2' }}>
          <div className="dash-chart-header">
            <div>
              <h2 style={{ fontSize: 15 }}>Waiter Performance</h2>
              <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 2 }}>Today's leaderboard</p>
            </div>
          </div>
          {loadingWaiter ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={40} />)}
            </div>
          ) : !waiterData?.length ? (
            <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 13 }}>No orders served yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {waiterData.slice(0, 8).map((w, i) => (
                <div key={w.waiterId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: i < waiterData.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <span style={{
                    width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700, flexShrink: 0,
                    background: i === 0 ? 'var(--amber-bg)' : i === 1 ? 'var(--surface2)' : 'var(--surface2)',
                    color: i === 0 ? 'var(--amber)' : 'var(--text-3)',
                  }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.waiterName}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{w.orderCount} orders</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{fmtShort(Number(w.totalRevenueUgx))}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Low stock */}
        <div className="card">
          <div className="dash-chart-header">
            <div>
              <h2 style={{ fontSize: 15 }}>Low Stock Alerts</h2>
            </div>
            {lowStock && lowStock.length > 0 && (
              <span className="badge badge-red">{lowStock.length} items</span>
            )}
          </div>
          {!lowStock ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={40} />)}
            </div>
          ) : lowStock.length === 0 ? (
            <div style={{ height: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 13, gap: 8 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth={1.5} style={{ width: 32, height: 32, opacity: 0.6 }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              All stock levels OK
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {lowStock.slice(0, 8).map((item) => {
                const pct = item.reorderLevel > 0 ? (item.stockQty / item.reorderLevel) * 100 : 100;
                const barColor = pct < 25 ? 'var(--red)' : pct < 60 ? 'var(--amber)' : 'var(--green)';
                return (
                  <div key={item.name} style={{ padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600, marginLeft: 8, flexShrink: 0 }}>{item.stockQty} {item.unit} left</span>
                    </div>
                    <div style={{ width: '100%', background: 'var(--surface2)', borderRadius: 99, height: 5 }}>
                      <div style={{ width: `${Math.min(pct, 100)}%`, height: 5, borderRadius: 99, background: barColor }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
